import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { SignaturePad } from "@/components/SignaturePad";
import { toast } from "sonner";
import {
  CalendarDays, Clock, Users, MapPin, Loader2, CheckCircle, Award,
  Banknote, UserPlus, ShieldAlert, User, Phone, Mail, Home, Heart, Zap, FileSignature
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const RELATIONSHIPS = [
  { value: "parent", label: "Parent" },
  { value: "guardian", label: "Legal Guardian" },
  { value: "grandparent", label: "Grandparent" },
  { value: "sibling", label: "Sibling (18+)" },
  { value: "relative", label: "Relative" },
  { value: "other", label: "Other" },
];

function calcAge(birthdate) {
  if (!birthdate) return null;
  try {
    const bd = new Date(birthdate);
    const now = new Date();
    let age = now.getFullYear() - bd.getFullYear();
    const m = now.getMonth() - bd.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < bd.getDate())) age--;
    return age;
  } catch { return null; }
}

export default function EventRegisterPublic() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    name: "", email: "", phone: "", birthdate: "", address: "",
  });
  const [guardian, setGuardian] = useState({
    guardian_name: "", guardian_birthdate: "", guardian_email: "",
    guardian_address: "", guardian_phone: "", guardian_relationship: "",
  });
  const [waiverConsent, setWaiverConsent] = useState(false);
  const [signatureData, setSignatureData] = useState(null);

  const attendeeAge = useMemo(() => calcAge(form.birthdate), [form.birthdate]);
  const isMinor = attendeeAge !== null && attendeeAge < 18;
  const guardianAge = useMemo(() => calcAge(guardian.guardian_birthdate), [guardian.guardian_birthdate]);
  const guardianTooYoung = guardianAge !== null && guardianAge < 18;

  const buildWaiverText = (evt, attendee, grd) => {
    const customTerms = evt?.custom_waiver_terms || "";
    return `WAIVER AND CONSENT FORM\n\nI, ${grd.guardian_name}, as the ${grd.guardian_relationship || "guardian"} of ${attendee.name} (born ${attendee.birthdate}), hereby give my consent for the minor to participate in "${evt?.title || "this event"}" organized at ${evt?.venue_name || "the venue"} on ${evt?.event_date || "the scheduled date"}.\n\nI acknowledge that participation in sports and physical activities involves inherent risks including but not limited to physical injury. I assume full responsibility for any risks of injury associated with participation in this event.\n\nI agree to release, indemnify, and hold harmless the event organizer, venue owner, coaches, and all associated personnel from any and all liability, claims, demands, or causes of action arising out of or related to any loss, damage, or injury sustained during or as a result of participation in this event.\n\nI confirm that the minor is in good physical condition and has no medical conditions that would prevent safe participation. I agree to notify the organizer of any relevant medical conditions.\n\nI authorize the event organizer and venue staff to seek emergency medical treatment for the minor if necessary.${customTerms ? "\n\nADDITIONAL TERMS:\n" + customTerms : ""}\n\nBy signing below, I confirm that I have read, understood, and agree to the terms of this waiver.`;
  };

  useEffect(() => {
    axios.get(`${API}/api/events/${eventId}`)
      .then(r => setEvent(r.data))
      .catch(() => toast.error("Event not found"))
      .finally(() => setLoading(false));
  }, [eventId]);

  const regCount = event?.registered_count || 0;
  const isFull = regCount >= (event?.capacity || 0);
  const isFree = event?.registration_type === "free" || (event?.price || 0) <= 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    if (!form.birthdate) { toast.error("Birthdate is required"); return; }
    if (isMinor && (!guardian.guardian_name || !guardian.guardian_phone)) {
      toast.error("Guardian name and phone are required for minors"); return;
    }
    if (isMinor && guardianTooYoung) {
      toast.error("Guardian must be at least 18 years old"); return;
    }
    if (isMinor && !waiverConsent) {
      toast.error("Guardian must consent to the waiver for minors"); return;
    }
    if (isMinor && !signatureData) {
      toast.error("Guardian signature is required for minors"); return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (isMinor) {
        Object.assign(payload, guardian);
        payload.waiver_consent = waiverConsent;
        payload.waiver_signature = signatureData;
        payload.waiver_consent_text = buildWaiverText(event, form, guardian);
      }
      const res = await axios.post(`${API}/api/events/${eventId}/register-public`, payload);
      setSuccess(res.data);
      toast.success("Registration successful!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setSubmitting(false);
  };

  if (loading || !event) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  if (event.status !== "ACTIVE") return (
    <div className="max-w-lg mx-auto px-4 py-20 text-center">
      <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-3" />
      <h1 className="font-heading text-2xl font-bold uppercase">Event Not Available</h1>
      <p className="text-muted-foreground mt-2">This event is not yet published or has ended.</p>
      <Link to="/events"><Button variant="outline" className="mt-6 rounded-full border-2">Browse Events</Button></Link>
    </div>
  );

  // Success state
  if (success) return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="registration-success">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      <h1 className="font-heading text-3xl font-bold uppercase">You're In!</h1>
      <p className="text-muted-foreground mt-2 mb-6">{success.message}</p>
      <div className="bg-white border-2 border-slate-100 p-5 text-left space-y-2 mb-6">
        <h3 className="font-heading text-sm font-bold uppercase">{event.title}</h3>
        <p className="text-sm text-muted-foreground flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />{event.event_date} | {event.start_time} - {event.end_time}</p>
        {event.venue_name && <p className="text-sm text-muted-foreground flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{event.venue_name}</p>}
        {!isFree && success.status === "PENDING_PAYMENT" && (
          <div className="bg-amber-50 border border-amber-200 p-3 mt-3">
            <p className="text-sm font-medium text-amber-800">Payment of ₱{success.amount} is required to confirm your spot.</p>
            <p className="text-xs text-amber-600 mt-1">The organizer will provide payment instructions.</p>
          </div>
        )}
      </div>
      <Link to={`/events/${eventId}`}><Button variant="outline" className="rounded-full border-2">View Event Details</Button></Link>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 md:py-12" data-testid="public-registration-page">
      {/* Event Header */}
      <div className="bg-white border-2 border-slate-100 p-6 mb-8" data-testid="event-header">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-5 h-5 text-court-orange" />
          <Badge className="bg-court-orange/10 text-court-orange text-[10px] font-heading uppercase">{event.event_type || "Event"}</Badge>
          {event.sport_type && <Badge variant="outline" className="text-[10px] font-heading uppercase">{event.sport_type}</Badge>}
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-3" data-testid="event-title">{event.title}</h1>
        {event.description && <p className="text-sm text-muted-foreground mb-4">{event.description}</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground"><CalendarDays className="w-4 h-4 text-court-orange" />{event.event_date}</div>
          <div className="flex items-center gap-1.5 text-muted-foreground"><Clock className="w-4 h-4" />{event.start_time} - {event.end_time}</div>
          <div className="flex items-center gap-1.5 text-muted-foreground"><Users className="w-4 h-4" />{regCount}/{event.capacity} spots</div>
          {event.venue_name && <div className="flex items-center gap-1.5 text-muted-foreground"><MapPin className="w-4 h-4" />{event.venue_name}</div>}
        </div>
        {event.coach_name && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
            <Award className="w-4 h-4 text-court-orange" />
            <span className="text-sm">Coach: <strong>{event.coach_name}</strong></span>
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-slate-100">
          {isFree ? (
            <Badge className="bg-green-100 text-green-700 text-xs font-heading uppercase">Free Event</Badge>
          ) : (
            <span className="font-heading text-lg font-bold text-court-orange flex items-center gap-1"><Banknote className="w-4 h-4" />₱{event.price} per person</span>
          )}
        </div>
      </div>

      {isFull ? (
        <div className="text-center py-12 bg-white border-2 border-dashed border-red-200">
          <Users className="w-10 h-10 text-red-300 mx-auto mb-2" />
          <h2 className="font-heading text-xl font-bold text-red-600 uppercase">Event Full</h2>
          <p className="text-sm text-muted-foreground mt-1">All spots have been taken.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {/* Attendee Details */}
          <div className="bg-white border-2 border-slate-100 p-6 mb-6" data-testid="attendee-form">
            <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-court-orange" />Your Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input data-testid="reg-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Juan Dela Cruz" className="border-2 rounded-none" required />
              </div>
              <div>
                <Label>Email Address *</Label>
                <Input data-testid="reg-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="juan@email.com" className="border-2 rounded-none" required />
              </div>
              <div>
                <Label>Mobile Phone</Label>
                <Input data-testid="reg-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+63 9XX XXX XXXX" className="border-2 rounded-none" />
              </div>
              <div>
                <Label>Birthdate *</Label>
                <Input data-testid="reg-birthdate" type="date" value={form.birthdate}
                  onChange={e => setForm(f => ({ ...f, birthdate: e.target.value }))}
                  className="border-2 rounded-none" required />
                {attendeeAge !== null && (
                  <p className={`text-xs mt-1 ${isMinor ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}>
                    Age: {attendeeAge} {isMinor && "— Minor (under 18)"}
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <Input data-testid="reg-address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="Street, City, Province" className="border-2 rounded-none" />
              </div>
            </div>
          </div>

          {/* Guardian Details (shown automatically for minors) */}
          {isMinor && (
            <div className="bg-amber-50 border-2 border-amber-200 p-6 mb-6" data-testid="guardian-form">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-2 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-600" />Guardian Details Required
              </h2>
              <p className="text-sm text-amber-700 mb-4">Since the attendee is under 18, an adult guardian (18+) must be provided.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Guardian Full Name *</Label>
                  <Input data-testid="guardian-name" value={guardian.guardian_name}
                    onChange={e => setGuardian(g => ({ ...g, guardian_name: e.target.value }))}
                    placeholder="Maria Santos" className="border-2 rounded-none bg-white" required />
                </div>
                <div>
                  <Label>Relationship to Attendee *</Label>
                  <Select value={guardian.guardian_relationship} onValueChange={v => setGuardian(g => ({ ...g, guardian_relationship: v }))}>
                    <SelectTrigger data-testid="guardian-relationship" className="border-2 rounded-none bg-white"><SelectValue placeholder="Select relationship" /></SelectTrigger>
                    <SelectContent>{RELATIONSHIPS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Guardian Birthdate *</Label>
                  <Input data-testid="guardian-birthdate" type="date" value={guardian.guardian_birthdate}
                    onChange={e => setGuardian(g => ({ ...g, guardian_birthdate: e.target.value }))}
                    className="border-2 rounded-none bg-white" required />
                  {guardianAge !== null && (
                    <p className={`text-xs mt-1 ${guardianTooYoung ? 'text-red-600 font-medium' : 'text-green-600'}`}>
                      {guardianTooYoung ? `Age ${guardianAge} — Guardian must be 18+` : `Age: ${guardianAge}`}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Guardian Mobile Phone *</Label>
                  <Input data-testid="guardian-phone" value={guardian.guardian_phone}
                    onChange={e => setGuardian(g => ({ ...g, guardian_phone: e.target.value }))}
                    placeholder="+63 9XX XXX XXXX" className="border-2 rounded-none bg-white" required />
                </div>
                <div>
                  <Label>Guardian Email</Label>
                  <Input data-testid="guardian-email" type="email" value={guardian.guardian_email}
                    onChange={e => setGuardian(g => ({ ...g, guardian_email: e.target.value }))}
                    placeholder="guardian@email.com" className="border-2 rounded-none bg-white" />
                </div>
                <div>
                  <Label>Guardian Address</Label>
                  <Input data-testid="guardian-address" value={guardian.guardian_address}
                    onChange={e => setGuardian(g => ({ ...g, guardian_address: e.target.value }))}
                    placeholder="Street, City, Province" className="border-2 rounded-none bg-white" />
                </div>
              </div>
            </div>
          )}

          {/* Waiver & Consent (for minors) */}
          {isMinor && guardian.guardian_name && !guardianTooYoung && (
            <div className="bg-white border-2 border-slate-200 p-6 mb-6" data-testid="waiver-form">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-2 flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-court-orange" />Waiver & Consent
              </h2>
              <p className="text-sm text-muted-foreground mb-4">As the guardian of a minor, you must read and sign the following waiver.</p>

              <div className="bg-slate-50 border border-slate-200 p-4 mb-4 max-h-52 overflow-y-auto text-xs leading-relaxed whitespace-pre-wrap" data-testid="waiver-text">
                {buildWaiverText(event, form, guardian)}
              </div>

              <div className="flex items-start gap-3 mb-4">
                <Checkbox id="waiver-consent" checked={waiverConsent}
                  onCheckedChange={v => setWaiverConsent(!!v)}
                  data-testid="waiver-consent-checkbox" className="mt-0.5" />
                <label htmlFor="waiver-consent" className="text-sm cursor-pointer">
                  I, <strong>{guardian.guardian_name}</strong>, have read and agree to the waiver and consent terms above. I confirm I am the <strong>{guardian.guardian_relationship || "guardian"}</strong> of <strong>{form.name}</strong> and am at least 18 years of age.
                </label>
              </div>

              {waiverConsent && (
                <div>
                  <Label className="font-heading text-sm font-bold mb-2 block">Guardian Signature *</Label>
                  <p className="text-[10px] text-muted-foreground mb-2">Draw your signature in the box below using your mouse or finger</p>
                  <SignaturePad onSignatureChange={setSignatureData} width={400} height={150} />
                </div>
              )}
            </div>
          )}

          {/* Submit */}
          <div className="flex items-center justify-between bg-white border-2 border-slate-200 p-6" data-testid="submit-section">
            <div className="text-sm text-muted-foreground">
              {isFree ? "Free registration" : <span>Registration fee: <strong className="text-court-orange">₱{event.price}</strong></span>}
            </div>
            <Button data-testid="submit-registration" type="submit" disabled={submitting || (isMinor && guardianTooYoung)}
              className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase text-xs px-8 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
              {submitting ? "Registering..." : "Register Now"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
